import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Activity } from "lucide-react";

/**
 * Admin Overview tile — deliverability pulse for the Zoho Security Pulse
 * (email OTP) system. Segments today's codes into sent / verified /
 * expired / failed / pending so ops can spot deliverability issues
 * before buyers complain.
 */
export function AdminPulseAnalyticsTile() {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/pulse-analytics/today").then((r) => setD(r.data)).catch(() => {});
  }, [api]);

  const total = d?.sent ?? 0;
  const active = total > 0;
  const mono = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

  const stat = (label, value, color) => (
    <div>
      <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">{label}</div>
      <div className="font-serif text-xl md:text-2xl tabular-nums mt-1" style={{ color, fontFamily: mono }}>
        {value}
      </div>
    </div>
  );

  return (
    <div
      data-testid="admin-pulse-analytics-tile"
      className={`w-full rounded-sm border p-6 md:p-7 mb-4 transition-colors ${active ? "border-[#5EE6D0]/50 bg-[#5EE6D0]/[0.05]" : "border-white/10 bg-black/40"}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="grid place-items-center w-9 h-9 rounded-full text-black shrink-0"
            style={{
              background: active ? "linear-gradient(135deg,#5EE6D0,#3CBFAE)" : "#1a1a1a",
              boxShadow: active ? "0 0 22px rgba(60,191,174,0.45)" : "none",
              color: active ? "#000" : "#5A5A55",
            }}>
            <ShieldCheck size={15} />
          </span>
          <div className="min-w-0">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em]" style={{ color: active ? "#5EE6D0" : "#8A8A82" }}>
              · Security Pulse · deliverability today ·
            </div>
            <div className="font-serif text-lg md:text-xl text-[#F5F5F0] mt-1 flex items-center gap-2">
              <Activity size={14} className="text-[#5EE6D0]" />
              {active ? `${d.verify_rate_pct}% verified · ${d.sent} sent` : "No Security Pulses issued today"}
            </div>
          </div>
        </div>
      </div>
      {active && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-4">
          {stat("Sent",     d.sent,     "#F5F5F0")}
          {stat("Verified", d.verified, "#5EE6D0")}
          {stat("Pending",  d.pending,  "#F5D67A")}
          {stat("Expired",  d.expired,  "#B87333")}
          {stat("Failed",   d.failed,   "#C0392B")}
        </div>
      )}
      {active && Object.keys(d.by_purpose || {}).length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/[0.08] flex flex-wrap gap-2">
          {Object.entries(d.by_purpose).map(([p, n]) => (
            <span key={p} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#C9C9C2]">
              <span className="text-[#5EE6D0]">{p.replace("_", " ")}</span>
              <span className="text-[#8A8A82]">·</span>
              <span className="tabular-nums">{n}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
